/**
 * Sends a website lead to the UniThink CRM.
 *
 * This is the only place the CRM is called from. A form supplies a `formId`
 * and whatever fields it collects; the server maps common field names
 * (fullName / studentName / name, phone / mobileNumber, ...) onto real CRM
 * columns and keeps anything it does not recognise, so a new form usually
 * needs no change on either side.
 *
 * Which counsellor receives the lead is decided by the CRM, under
 * Lead Routing — never by the form. This file is public, so anything it
 * claimed about ownership could be tampered with.
 *
 * ── Safety contract ──────────────────────────────────────────────────────
 * While forms still double-write to Google Sheets, this call must never be
 * able to break that. So `submitLead`:
 *
 *   • never throws — every failure comes back as { ok: false }
 *   • gives up after `timeoutMs` so a slow CRM cannot hang the button
 *     (the Render instance may cold-start after a period of inactivity)
 *   • does nothing at all when REACT_APP_CRM_URL is unset, so a missing
 *     env var degrades to the old Sheets-only behaviour instead of erroring
 *
 * Callers should use Promise.allSettled, never Promise.all — `all` rejects on
 * the first failure and would take the Sheets result down with it.
 */

const CRM_URL = process.env.REACT_APP_CRM_URL;
const FORM_KEY = process.env.REACT_APP_CRM_FORM_KEY;

const DEFAULT_TIMEOUT_MS = 10000;

const TRACKING_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'fbclid',
];

/** Ad-campaign parameters from the current URL, so leads keep their attribution. */
function trackingParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    TRACKING_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) found[key] = value;
    });
    return found;
  } catch {
    return {};
  }
}

/**
 * @param {object}  options
 * @param {string}  options.formId    Short name for this form, e.g. "mba-bangalore".
 *                                    Becomes the campaign in the CRM and the key
 *                                    its Lead Routing rule is matched on.
 * @param {object}  options.fields    Whatever the form collected.
 * @param {number} [options.timeoutMs]
 * @returns {Promise<{ok: boolean, skipped?: boolean, status?: number, reason?: string}>}
 */
export async function submitLead({
  formId,
  fields = {},
  timeoutMs = DEFAULT_TIMEOUT_MS,
  retries = 0,
}) {
  if (!CRM_URL) return { ok: false, skipped: true, reason: 'REACT_APP_CRM_URL is not set' };
  if (!formId) return { ok: false, skipped: true, reason: 'formId is required' };

  let last;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    last = await attemptSubmit(formId, fields, timeoutMs);
    if (last.ok) return last;
    // A status code means the server answered and disagreed (bad key, bad
    // payload); repeating it would only fail again. Retry only when the
    // request never landed - a timeout or a dropped connection, which is what
    // a cold-starting server looks like.
    if (last.status) return last;
  }
  return last;
}

async function attemptSubmit(formId, fields, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${CRM_URL.replace(/\/+$/, '')}/public/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(FORM_KEY ? { 'X-UniThink-Form-Key': FORM_KEY } : {}),
      },
      // form_id and source_url go last so a field can never overwrite them.
      body: JSON.stringify({
        ...trackingParams(),
        ...fields,
        form_id: formId,
        source_url: window.location.href,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      return { ok: false, status: response.status, reason: `CRM responded ${response.status}` };
    }
    return { ok: true };
  } catch (err) {
    const timedOut = err && err.name === 'AbortError';
    return { ok: false, reason: timedOut ? 'timed out' : String((err && err.message) || err) };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fire-and-forget wrapper for forms that still double-write to Google Sheets.
 * Not for CRM-only forms - there, a failure must reach the visitor so they can
 * try again, so call submitLead directly and act on the result.
 * Resolves to a result object and never rejects, so it is safe inside
 * Promise.allSettled next to the Sheets call.
 */
export function submitLeadQuietly(options) {
  return submitLead(options).then((result) => {
    if (!result.ok && !result.skipped) {
      // Visible in the console for debugging; never shown to the visitor,
      // because Sheets still has their submission.
      console.warn('[crm] lead not recorded:', result.reason);
    }
    return result;
  });
}
